import React, { useEffect } from 'react';
import { useScreenInit } from '../../useScreenInit';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
const processSteps = [{
  title: 'Site survey & assessment',
  desc: 'Detailed evaluation of your building, current systems, and thermal loads.'
}, {
  title: 'System design & load calculations',
  desc: 'Bespoke engineering to ensure perfect sizing and optimal efficiency.'
}, {
  title: 'Equipment selection',
  desc: 'Specifying the right units from premium brands like Daikin and Toshiba.'
}, {
  title: 'Professional installation',
  desc: 'F-Gas certified, in-house engineers working to the highest standards.'
}, {
  title: 'Full commissioning & testing',
  desc: 'Rigorous checks to guarantee performance and compliance before handover.'
}, {
  title: 'Handover & documentation',
  desc: 'Comprehensive training, O&M manuals, and warranty registration.'
}];
const sectors = ['Office', 'Retail', 'Hospitality', 'Healthcare', 'Education', 'Industrial'];
export function Installation() {
  const screenInit = useScreenInit();
  useEffect(() => {
    document.title = 'Commercial AC Installation | AccuTemp';
  }, []);
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2310">
      {/* Hero */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2311">
        <div className="container mx-auto px-6" data-id="element-2312">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2313">
            <div className="lg:col-span-7" data-id="element-2314">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2315">
                Services / Installation
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark" data-id="element-2316">
                Commercial AC installation designed around your building
              </motion.h1>
            </div>
            <div className="lg:col-span-5 lg:pt-12" data-id="element-2317">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2318">
                We do not just fit units. We design bespoke systems using proper
                load calculations, manufacturer-trained engineers, and full
                commissioning — so your installation lasts.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Design-led approach */}
      <section className="bg-white py-24" data-id="element-2319">
        <div className="container mx-auto px-6" data-id="element-2320">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-2321">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2322">
              <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-2323">
                Why design matters
              </h2>
              <div className="space-y-4 text-primary-dark/70 leading-relaxed" data-id="element-2324">
                <p data-id="element-2325">
                  Most commercial air conditioning failures trace back to poor
                  initial installation. Wrong sizing, inadequate airflow
                  modelling, and skipped commissioning lead to inefficient
                  systems that break down prematurely.
                </p>
                <p data-id="element-2326">
                  At AccuTemp, we start with the building, not the catalogue.
                  Our in-house design team conducts thorough thermal load
                  calculations and space assessments to engineer a system that
                  delivers exact cooling and heating requirements without
                  wasting energy.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-3xl overflow-hidden aspect-[4/3]" data-id="element-2327">
              <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80" alt="Commercial HVAC installation" className="w-full h-full object-cover" data-id="element-2328" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2329">
        <div className="container mx-auto px-6" data-id="element-2330">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2331">
            Our installation process
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2332">
            {processSteps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-8 rounded-3xl shadow-sm border border-primary-dark/5 relative overflow-hidden group" data-id="element-2333">
                <div className="text-6xl font-serif text-primary-dark/5 absolute -top-4 -right-2 group-hover:text-accent/10 transition-colors duration-300" data-id="element-2334">
                  0{index + 1}
                </div>
                <h3 className="text-xl font-medium mb-3 relative z-10" data-id="element-2335">
                  {step.title}
                </h3>
                <p className="text-primary-dark/60 text-sm leading-relaxed relative z-10" data-id="element-2336">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="bg-white py-24" data-id="element-2337">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-2338">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-6" data-id="element-2339">
            Manufacturer partnerships
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed mb-12" data-id="element-2340">
            As a Daikin D1+ Premier Partner, alongside strong relationships with
            Toshiba and Mitsubishi, we offer extended warranties of up to 7
            years. Our engineers receive direct manufacturer training, ensuring
            your system is installed exactly to specification with priority
            access to parts.
          </motion.p>
        </div>
      </section>

      {/* Types of systems */}
      <section className="bg-[#E8F0F8]/50 py-24 border-t border-primary-dark/5" data-id="element-2341">
        <div className="container mx-auto px-6" data-id="element-2342">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2343">
            Types of systems we install
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2344">
            <Link to="/services/ducted" className="group bg-white p-8 rounded-3xl shadow-sm border border-primary-dark/5 hover:border-accent/30 transition-all" data-id="element-2345">
              <h3 className="text-xl font-serif mb-3 group-hover:text-accent transition-colors" data-id="element-2346">
                Ducted Air Conditioning
              </h3>
              <p className="text-primary-dark/70 text-sm mb-6" data-id="element-2347">
                Discreet, hidden air conditioning for offices and commercial
                interiors where appearance matters.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-accent" data-id="element-2348">
                View system{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2349" />
              </span>
            </Link>
            <Link to="/services/cassette" className="group bg-white p-8 rounded-3xl shadow-sm border border-primary-dark/5 hover:border-accent/30 transition-all" data-id="element-2350">
              <h3 className="text-xl font-serif mb-3 group-hover:text-accent transition-colors" data-id="element-2351">
                Cassette Air Conditioning
              </h3>
              <p className="text-primary-dark/70 text-sm mb-6" data-id="element-2352">
                Ceiling-mounted system with even airflow, ideal for offices,
                shops and customer-facing spaces.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-accent" data-id="element-2353">
                View system{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2354" />
              </span>
            </Link>
            <Link to="/services/wall-mounted" className="group bg-white p-8 rounded-3xl shadow-sm border border-primary-dark/5 hover:border-accent/30 transition-all" data-id="element-2355">
              <h3 className="text-xl font-serif mb-3 group-hover:text-accent transition-colors" data-id="element-2356">
                Wall Mounted Air Conditioning
              </h3>
              <p className="text-primary-dark/70 text-sm mb-6" data-id="element-2357">
                Practical commercial solution for larger rooms and spaces where
                wall-mounted is the right fit.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-accent" data-id="element-2358">
                View system{' '}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2359" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="bg-[#D4DDE8] py-24" data-id="element-2360">
        <div className="container mx-auto px-6" data-id="element-2361">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2362">
            Sectors we install for
          </motion.h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto" data-id="element-2363">
            {sectors.map((sector, index) => <motion.div key={sector} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-white px-8 py-4 rounded-full text-primary-dark font-medium shadow-sm border border-primary-dark/5" data-id="element-2364">
                {sector}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-dark py-24 text-center" data-id="element-2365">
        <div className="container mx-auto px-6" data-id="element-2366">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2367">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-10" data-id="element-2368">
              Ready to discuss your installation?
            </h2>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2369">
              Get a free site survey
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2370" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}